import { useQuery } from '@tanstack/react-query'
import { kybService } from '@/services/kybService'
import type { OrgKybStatus } from '@/types/kyb'

/**
 * React Query layer for organization KYB verification.
 *
 * Query-key shape mirrors `organizationKeys` in `useOrganization.ts`: a root
 * `all` tuple with narrower factories spread beneath it, so one slice or a whole
 * subtree can be invalidated by prefix.
 *
 * NOTE, on record: this feeds status *displays* only. It does not move the route
 * gate — `App.tsx` holds the attestation status in plain `useState` and fetches
 * it once per auth transition, so invalidating anything here changes nothing
 * about where the router sends the user. The signup wizard calls `kybService`
 * directly for the same reason.
 */
export const kybKeys = {
  all: ['kyb'] as const,
  status: (orgId: string) => [...kybKeys.all, 'status', orgId] as const,
}

/**
 * `apiClient` already retries internally, and these endpoints answer 403 by
 * design for non-managers. Retrying on top of that just multiplies the noise.
 */
const NO_RETRY = { retry: false } as const

/** One organization's KYB state. `null` until the first successful load. */
export const useOrgKybStatus = (orgId: string, enabled = true) => {
  const query = useQuery({
    queryKey: kybKeys.status(orgId),
    queryFn: () => kybService.getStatus(orgId),
    enabled: !!orgId && enabled,
    staleTime: 60_000,
    ...NO_RETRY,
  })

  return {
    kybStatus: (query.data ?? null) as OrgKybStatus | null,
    isLoading: query.isLoading,
    error: toMessage(query.error),
    refetch: query.refetch,
  }
}

const toMessage = (error: unknown): string | null => {
  if (!error) return null
  return error instanceof Error ? error.message : String(error)
}
